import {
  TeamInboxFanIn,
  TeamInboxOrgClient,
  type TeamInboxOrgDescriptor,
  type TeamInboxSnapshot,
} from '@nimbalyst/collab-bundle/inbox';

import { consoleConfig } from '../config';
import type { TeamJwt } from '../auth/teamJwt';
import { unreadInboxDeliveryIds } from './requestsInboxModel';

/**
 * Presence liveness cadence.
 *
 * `TEAM_PRESENCE_STALE_MS` on the server is 75 seconds, so a client that opens
 * the inbox socket and then stays silent is shown as **offline to every desktop
 * teammate** inside a minute and a quarter while still holding a live
 * connection. A web-only reviewer reading a request is present and must read
 * that way, so this client heartbeats on the same 30s cadence desktop does.
 * `TeamInboxOrgClient` defaults to this value; it is passed explicitly because
 * an invisible default is exactly how this gets silently lost.
 */
export const TEAM_PRESENCE_HEARTBEAT_INTERVAL_MS = 30_000;

export interface BrowserTeamInboxClientOptions {
  org: TeamInboxOrgDescriptor;
  /**
   * The **team** JWT for this org, never the personal one. The personal JWT is
   * for personal/mobile sync; every team room — this inbox included — is
   * authorized by the org-scoped team session, and Stytch B2B issues a
   * different member id per org.
   */
  getTeamJwt: () => Promise<TeamJwt>;
  createWebSocket?: (url: string) => WebSocket;
  now?: () => number;
  heartbeatIntervalMs?: number;
}

/**
 * Browser owner of the team inbox connection for one organization.
 *
 * `TeamInboxFanIn` already fans in across N organizations; the console routes
 * one org at a time, so it is started with a single descriptor. Keeping the
 * fan-in rather than talking to `TeamInboxOrgClient` directly is what makes a
 * cross-org badge, and every later delivery kind, additive here.
 *
 * There is no persistence layer. The Durable Object is authoritative and a
 * browser tab has no restart-continuity requirement worth a schema.
 */
export class BrowserTeamInboxClient {
  private readonly fanIn: TeamInboxFanIn;
  private readonly org: TeamInboxOrgDescriptor;
  private destroyed = false;

  constructor(options: BrowserTeamInboxClientOptions) {
    this.org = options.org;
    this.fanIn = new TeamInboxFanIn({
      now: options.now,
      createClient: (org) => new TeamInboxOrgClient({
        serverUrl: consoleConfig.collabApiBaseUrl,
        org,
        getTeamJwt: options.getTeamJwt,
        createWebSocket: options.createWebSocket,
        heartbeatIntervalMs: options.heartbeatIntervalMs
          ?? TEAM_PRESENCE_HEARTBEAT_INTERVAL_MS,
        now: options.now,
      }),
    });
  }

  getSnapshot = (): TeamInboxSnapshot => this.fanIn.getSnapshot();

  subscribe = (listener: () => void): (() => void) => this.fanIn.subscribe(listener);

  start(): Promise<void> {
    if (this.destroyed) {
      return Promise.reject(new Error('Browser team inbox client has been destroyed'));
    }
    return this.fanIn.start([this.org]);
  }

  markRead(deliveryIds: string[]): Promise<void> {
    return deliveryIds.length > 0 ? this.fanIn.markRead(deliveryIds) : Promise.resolve();
  }

  /**
   * Explicit ids rather than the protocol's "empty means everything", so an
   * unavailable delivery or agent-only feedback wake is never consumed by a
   * surface that did not admit it.
   */
  markAllVisibleRead(): Promise<void> {
    return this.markRead(unreadInboxDeliveryIds(this.getSnapshot().deliveries));
  }

  dismiss(deliveryId: string): Promise<void> {
    return this.fanIn.dismiss(deliveryId);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.fanIn.destroy();
  }
}
